/**
 * ANIMATE mode (right column): the selected bone, the IK switches, the open clip and the read-only
 * preview models loaded beside it.
 *
 * Every control here is one this panel OWNS. fps / duration / loop live in the transport and Save
 * lives in the toolbar; the duplicates that used to sit in the Clip section made the column 813px
 * tall and left Event and Preview models below the fold even at 1080p (audit §B).
 *
 * The Euler inputs are a *display* convenience only — the quaternion in `livePose` stays
 * authoritative (SPEC "Rotation Representation"). Editing a field rebuilds a quaternion from all
 * three fields, so a round-trip through gimbal-equivalent angles never accumulates drift in the
 * stored data.
 */

import { Euler, MathUtils, Quaternion } from 'three';

import { IK_CHAINS } from '../../shared';
import type { CanonicalBoneId, ProjectRecord, QuaternionTuple } from '../../shared';
import type { EditorContext } from '../context';
import type { Controllers } from '../controllers/types';
import { resolveEvent } from '../actions';
import { dialogs } from '../dialogs';
import { MAX_COMPANIONS } from '../types';
import './inspector.css';
import {
  button,
  el,
  icon,
  iconButton,
  numberInput,
  pill,
  select,
  switchField,
  textInput,
} from './dom';
import {
  confirmDiscardDirtyClip,
  createPanel,
  field,
  note,
  readonlyField,
  runAsync,
  section,
  type PanelHandle,
} from './panel';

const IDENTITY: QuaternionTuple = [0, 0, 0, 1];
const AXES = ['X', 'Y', 'Z'] as const;

function toEulerDegrees(rotation: QuaternionTuple): [number, number, number] {
  const euler = new Euler().setFromQuaternion(
    new Quaternion(rotation[0], rotation[1], rotation[2], rotation[3]),
    'XYZ',
  );
  return [
    MathUtils.radToDeg(euler.x),
    MathUtils.radToDeg(euler.y),
    MathUtils.radToDeg(euler.z),
  ];
}

function fromEulerDegrees(degrees: readonly number[]): QuaternionTuple {
  const q = new Quaternion().setFromEuler(
    new Euler(
      MathUtils.degToRad(degrees[0]),
      MathUtils.degToRad(degrees[1]),
      MathUtils.degToRad(degrees[2]),
      'XYZ',
    ),
  );
  q.normalize();
  return [q.x, q.y, q.z, q.w];
}

export function mountInspectorPanel(
  container: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
): PanelHandle {
  const { store } = ctx;

  /**
   * "Hips height" — the vertical Hips offset, the one channel in this format that is not a
   * rotation (Hips only; every other bone shows three Euler fields and nothing else).
   *
   * Shown in CENTIMETRES OF THE OPEN MODEL, not in the normalized units the clip stores: "−20 cm"
   * is a crouch an animator can picture, "−0.31 leg lengths" is arithmetic. The conversion needs
   * the rig's bind leg length, so the field is disabled — with a tooltip that says why — whenever
   * there is no bound rig or the channel is off on it (`legLength() === 0`, i.e. an unmapped Hips
   * or foot). The normalized value stays visible in the tooltip, because that is what a keyframe
   * and a bug report actually carry.
   */
  function hipsHeightField(): HTMLElement {
    const state = store.getState();
    const legLength = ctx.session.rig?.legLength() ?? 0;
    const usable = legLength > 0;
    const centimetres = usable ? state.liveHipsY * legLength * 100 : 0;

    const input = numberInput(
      Number(centimetres.toFixed(1)),
      (value) => store.actions.setLiveHipsY(value / 100 / legLength),
      {
        step: '0.5',
        'data-hips-y': true,
        disabled: !usable,
        title: usable
          ? `hipsY ${state.liveHipsY.toFixed(3)} · leg lengths (leg ${(legLength * 100).toFixed(1)} cm)`
          : 'Needs a bound rig with a mapped Hips and both feet — the hips height channel is off.',
      },
    );
    return field('Hips height (cm)', input);
  }

  function boneSection(bone: CanonicalBoneId | null): HTMLElement {
    if (!bone) return section('Bone', [note('No bone selected.')], { icon: 'joint' });

    const state = store.getState();
    const rotation = state.livePose[bone] ?? IDENTITY;
    const degrees = toEulerDegrees(rotation);
    const frame = Math.round(state.playhead.frame);
    const keyed = (state.clip?.tracks[bone]?.keyframes ?? []).some((k) => k.frame === frame);

    const inputs = AXES.map((axis, index) =>
      field(
        `Rotation ${axis}°`,
        numberInput(
          Number(degrees[index].toFixed(3)),
          (value) => {
            const next = [...degrees];
            next[index] = value;
            store.actions.setLivePoseBone(bone, fromEulerDegrees(next));
          },
          { step: '1', 'data-euler': axis },
        ),
      ),
    );

    return section(
      'Bone',
      [
        ...inputs,
        ...(bone === 'Hips' ? [hipsHeightField()] : []),
        note('Euler view of the stored quaternion.', 'note dim'),
        // One row, one primary action: keying is what this section is for. The two rare
        // actions are icon-only so the row never wraps in a 280px column.
        el('div', { class: 'buttons' }, [
          button([icon('key'), 'Key bone'], () => store.actions.keyBone(bone), {
            class: 'primary',
            disabled: !state.clip,
            'data-action': 'key-bone',
          }),
          iconButton('trash', 'Delete key at frame', () => store.actions.deleteKey(bone, frame), {
            disabled: !keyed,
            'data-action': 'delete-key',
          }),
          iconButton('reset', 'Reset bone to rest', () => store.actions.setLivePoseBone(bone, IDENTITY), {
            'data-action': 'reset-bone',
          }),
        ]),
      ],
      { meta: bone, icon: 'joint', key: 'Bone' },
    );
  }

  function ikSection(): HTMLElement {
    const state = store.getState();
    const enabled = state.ik.enabled;

    // Four chains in one wrapping pill row instead of four full-width checkbox slots: the
    // section is a mode switch plus a small multi-select, and it used to cost five lines.
    const pills = IK_CHAINS.map((chain) => {
      const target = state.ik.targets[chain.name];
      const active = target?.active ?? false;
      return pill(chain.name, active, () => store.actions.activateIk(chain.name, !active), {
        disabled: !enabled || !target,
        title: target ? '' : 'Drag the IK handle in the viewport to create a target',
        'data-chain': chain.name,
      });
    });

    const active = IK_CHAINS.filter((chain) => state.ik.targets[chain.name]?.active).length;

    return section(
      'IK',
      [
        switchField('Enable IK', enabled, (checked) => store.actions.setIkEnabled(checked), {
          'data-role': 'ik-enabled',
        }),
        el('div', { class: 'buttons' }, pills),
        note('Targets snap to the current pose when you first drag their handle.', 'note dim'),
      ],
      { icon: 'target', meta: active > 0 ? `${active} active` : undefined },
    );
  }

  /**
   * Both "replace the open clip" actions ask the SAME two questions in the same order: may I throw
   * your edits away, and then what do you want. Discard first, always — asking for a name or a
   * pick first and cancelling on the discard question throws the answer the user just typed away.
   * `confirmDiscardDirtyClip` resolves `true` synchronously when nothing is dirty, so the clean
   * case still shows exactly one dialog.
   *
   * `confirm: () => true` is not a bypass: the question has already been asked, through the one
   * wording every panel shares, and the controller's own `window.confirm` fallback must never be
   * reached from the UI.
   */
  async function newClip(): Promise<void> {
    if (!(await confirmDiscardDirtyClip(store))) return;
    const name = await dialogs.text({
      title: 'New clip',
      label: 'Name',
      value: 'new clip',
      confirmLabel: 'Create',
    });
    if (!name) return;
    controllers.library.newClip(name, { confirm: () => true });
  }

  async function openClip(): Promise<void> {
    if (!(await confirmDiscardDirtyClip(store))) return;
    const clips = store.getState().library.clips;
    const id = await dialogs.pick({
      title: 'Open clip',
      options: clips.map((clip) => ({
        value: clip.id,
        label: clip.name,
        meta: `${clip.folder} · ${clip.durationFrames}f`,
      })),
      emptyMessage: 'Library is empty',
    });
    if (!id) return;
    runAsync(controllers.library.loadClip(id));
  }

  /**
   * Name, length and the two ways to change which clip is open — nothing else.
   *
   * fps, duration and loop are the TRANSPORT's (they belong next to the playhead they change),
   * and Save is the toolbar's. Carrying second copies here cost this panel five slots and
   * pushed Event and Preview models below the fold (audit §B, 813–964px of inspector).
   */
  function clipSection(): HTMLElement {
    const state = store.getState();
    const clip = state.clip;
    const rows: HTMLElement[] = [];

    if (!clip) {
      rows.push(note('No clip open.'));
    } else {
      rows.push(
        field(
          'Name',
          textInput(clip.name, (name) => store.actions.setClipName(name), {
            'data-role': 'clip-name',
          }),
        ),
      );
      rows.push(
        readonlyField(
          'Length',
          `${clip.durationFrames} f · ${(clip.durationFrames / clip.fps).toFixed(2)} s @ ${clip.fps} fps`,
        ),
      );
    }

    rows.push(
      el('div', { class: 'buttons' }, [
        button([icon('plus'), 'New clip'], () => runAsync(newClip()), {
          'data-action': 'new-clip',
        }),
        button([icon('open'), 'Open…'], () => runAsync(openClip()), {
          disabled: state.library.clips.length === 0,
          title:
            state.library.clips.length === 0
              ? 'The library has no saved clips yet'
              : 'Open a clip from the library',
          'data-action': 'open-clip',
        }),
      ]),
    );

    return section('Clip', rows, {
      icon: 'film',
      key: 'Clip',
      meta: clip ? `${clip.name}${state.clipDirty ? ' ●' : ''}` : undefined,
    });
  }

  /**
   * The selected animation event. Shown only when one is selected, because the section is about
   * a marker and there is nothing useful to render without one — the band itself is where markers
   * are created.
   */
  function eventSection(): HTMLElement | null {
    const state = store.getState();
    const ref = state.selectedEvent;
    const event = resolveEvent(state, ref);
    // A ref can outlive the marker it names (undo, an fps change that moved it). Rendering the
    // stale ref's fields would offer edits that silently do nothing.
    if (!ref || !event) return null;

    const rows: HTMLElement[] = [];

    rows.push(
      field(
        'Name',
        textInput(event.name, (name) => store.actions.renameEvent(ref, name), {
          'data-role': 'event-name',
        }),
      ),
    );
    rows.push(
      field(
        'Frame',
        numberInput(event.frame, (frame) => store.actions.moveEvent(ref, Math.round(frame)), {
          step: '1',
          min: '0',
          'data-role': 'event-frame',
        }),
      ),
    );
    rows.push(readonlyField('Seconds', (event.frame / (state.clip?.fps ?? 30)).toFixed(3)));

    // `data` is a flat map of primitives. One row per key, plus a blank row to add one.
    const data = event.data ?? {};
    for (const key of Object.keys(data).sort()) {
      rows.push(
        field(
          key,
          textInput(String(data[key]), (value) => {
            const next = { ...data };
            if (value.trim().length === 0) delete next[key];
            else next[key] = value;
            store.actions.setEventData(ref, Object.keys(next).length > 0 ? next : undefined);
          }),
        ),
      );
    }

    rows.push(
      el('div', { class: 'buttons' }, [
        button(
          [icon('plus'), 'Add field'],
          () => {
            runAsync(dialogs.text({ title: 'Add field', label: 'Field name' }), (key) => {
              if (!key) return;
              store.actions.setEventData(ref, { ...data, [key]: '' });
            });
          },
          { 'data-action': 'add-event-field' },
        ),
        iconButton('trash', 'Delete event', () => store.actions.removeEvent(ref), {
          'data-action': 'delete-event',
        }),
      ]),
    );

    return section('Event', rows, { icon: 'flag', meta: event.name });
  }

  function render(root: HTMLElement): void {
    root.appendChild(boneSection(store.getState().selection.bone));
    root.appendChild(ikSection());
    root.appendChild(clipSection());
    const events = eventSection();
    if (events) root.appendChild(events);
  }

  const instance = createPanel(container, ctx, render, {
    className: 'inspector',
    slices: [
      (s) => s.selection.bone,
      (s) => (s.selection.bone ? s.livePose[s.selection.bone] : null),
      // The hips height is un-keyed posing exactly like a rotation, so the panel has to rebuild
      // when the viewport handle (or a scrub, or undo) moves it — the slice above only watches
      // the selected bone's quaternion and would leave the field reading a stale number.
      (s) => s.liveHipsY,
      (s) => s.clip,
      (s) => s.clipDirty,
      (s) => s.selectedEvent,
      (s) => Math.round(s.playhead.frame),
      // `Open…` is enabled by what the library holds, and `saveCurrentClip` refreshes the
      // library AFTER it marks the clip saved — without this the button would still read the
      // pre-save (empty) list on the rebuild `clipDirty` triggers.
      (s) => s.library.clips,
      (s) => s.ik,
    ],
  });

  // Preview models are a SEPARATE panel instance in the same column (like RIG's two left panels),
  // not a fourth section of this one. `createPanel` rebuilds its whole subtree on every
  // invalidation, and the inspector invalidates on `playhead.frame` — at 30 Hz during playback
  // that would destroy the companion `<select>` out from under a user who has it open. Its own
  // instance subscribes only to what it actually shows.
  const preview = mountPreviewPanel(container, ctx, controllers);

  return {
    dispose(): void {
      preview.dispose();
      instance.dispose();
    },
  };
}

/**
 * "Preview models": the read-only companions loaded beside the open project.
 *
 * The list of saved projects is read through the project controller, like every other panel —
 * panels never reach IndexedDB themselves. It is refreshed lazily and never blocks a rebuild: a
 * controller that cannot produce it (or a fixture that does not have it) costs the section its
 * "Add…" options and nothing else.
 */
function mountPreviewPanel(
  container: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
): PanelHandle {
  const { store } = ctx;

  let savedProjects: ProjectRecord[] = [];
  /** Which project the `<select>` is on; kept across rebuilds so choosing then adding works. */
  let addChoice = '';

  function companionSection(): HTMLElement {
    const state = store.getState();
    const companions = state.companions;
    const rows: HTMLElement[] = [];

    if (companions.length === 0) {
      rows.push(note('No preview models. Add one to compare this clip on another character.'));
    }
    for (const companion of companions) {
      rows.push(
        el('div', { class: 'row', 'data-companion': companion.projectId }, [
          el('span', { class: 'grow', text: companion.name }),
          button([icon('trash')], () => controllers.preview.removeCompanion(companion.projectId), {
            title: `Remove "${companion.name}"`,
            'data-action': 'remove-companion',
          }),
        ]),
      );
    }

    const taken = new Set([
      ...companions.map((c) => c.projectId),
      ...(state.project ? [state.project.id] : []),
    ]);
    const options = savedProjects
      .filter((project) => !taken.has(project.id))
      .map((project) => ({ value: project.id, label: project.name }));
    if (!options.some((option) => option.value === addChoice)) addChoice = options[0]?.value ?? '';

    const full = companions.length >= MAX_COMPANIONS;
    const fullTitle = `Up to ${MAX_COMPANIONS} preview models at a time — remove one first.`;

    rows.push(
      el('div', { class: 'row' }, [
        select(
          options.length > 0 ? options : [{ value: '', label: 'No other saved projects' }],
          addChoice,
          (value) => {
            addChoice = value;
          },
          {
            class: 'grow',
            disabled: full || options.length === 0,
            title: full ? fullTitle : '',
            'data-role': 'companion-choice',
          },
        ),
        button(
          [icon('plus'), 'Add…'],
          () => {
            if (!addChoice) return;
            runAsync(controllers.preview.addCompanion(addChoice), refreshProjects);
          },
          {
            disabled: full || options.length === 0,
            title: full ? fullTitle : 'Load this project beside the open one',
            'data-action': 'add-companion',
          },
        ),
      ]),
    );

    rows.push(
      note('Preview models are read-only: they play the open clip and are never exported.', 'note dim'),
    );

    return section('Preview models', rows, {
      icon: 'layers',
      meta: `${companions.length}/${MAX_COMPANIONS}`,
      // Bottom of the column and rarely used: it opens closed so Bone / IK / Clip / Event all
      // fit above the fold at 720px. One click and the user's own choice sticks from then on.
      defaultCollapsed: true,
    });
  }

  const instance = createPanel(
    container,
    ctx,
    (root) => {
      root.appendChild(companionSection());
    },
    {
      className: 'preview',
      // Only what the section shows. In particular NOT the playhead: a companion list has no
      // reason to be rebuilt sixty times a second while a clip plays.
      slices: [(s) => s.companions, (s) => s.project],
      events: ['companions-changed'],
    },
  );

  function refreshProjects(): void {
    controllers.project.listProjects().then(
      (list) => {
        savedProjects = [...list].sort((a, b) => a.name.localeCompare(b.name));
        instance.schedule();
      },
      () => {
        /* already reported by the controller; the section simply offers nothing to add */
      },
    );
  }
  refreshProjects();

  return instance;
}
